import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LeadPeerStat, LeadRow, RelatedLead } from '@/api/types';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Callout } from '@/components/Callout';
import { LeadScoreCard } from '@/components/LeadScoreCard';
import { LeadStatusChip } from '@/components/LeadPipeline';
import { LeadTimeline } from '@/components/LeadTimeline';
import { formatDateTime, formatDuration, formatMoney } from '@/components/format';
import { useLeadProfile } from '@/hooks/useLeads';

/**
 * Everything known about one lead.
 *
 * <b>Four questions, in the order a person asks them.</b> Who is this and how did they get here
 * (identity + attribution); how good is it and why (the score, with its workings); what actually
 * happened (the timeline); and is this normal (peers, then lookalikes). Anything that would answer
 * a fifth question nobody asked is left off.
 *
 * The page is read-only. Editing stage, value and owner lives in the table, where you can see the
 * pipeline you are moving the lead within — two edit surfaces for one row is how they diverge.
 */
export function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useLeadProfile(leadId);

  if (isLoading) {
    return <div className="empty-state"><span className="spinner" /></div>;
  }

  if (isError) {
    return (
      <Callout tone="error">
        Could not load this lead. {(error as Error)?.message}
      </Callout>
    );
  }

  if (!data) {
    return (
      <div className="empty-state t-body">
        This lead no longer exists.
        <div style={{ marginTop: 12 }}>
          <Link className="btn btn-ghost" to="/leads">Back to leads</Link>
        </div>
      </div>
    );
  }

  const lead = data.lead;
  const name = lead.name ?? lead.email ?? lead.phone ?? '(anonymous lead)';
  const canManage = hasPermission(PERMISSIONS.leadsManage);

  return (
    <>
      <div className="lead-detail-head">
        <div>
          <Link to="/leads" className="t-caption subtle lead-back">← All leads</Link>
          <h1 className="t-display" style={{ margin: '4px 0 0' }}>{name}</h1>
          <div className="lead-detail-sub t-caption subtle">
            <LeadStatusChip status={lead.status} />
            <span>{lead.source === 'chat' ? 'Widget chat' : 'Landing form'}</span>
            <span>Captured {formatDateTime(lead.captured_at)}</span>
            {lead.is_missed && <strong className="lead-missed">Never answered</strong>}
          </div>
        </div>

        <div className="lead-detail-actions">
          {lead.email && <a className="btn btn-ghost" href={`mailto:${lead.email}`}>E-mail</a>}
          {lead.phone && <a className="btn btn-ghost" href={`tel:${lead.phone}`}>Call</a>}
          {canManage && (
            <Link className="btn btn-ghost" to="/leads">Edit in pipeline</Link>
          )}
        </div>
      </div>

      <div className="lead-detail-grid">
        <div className="lead-detail-main">
          <LeadScoreCard score={data.score} lead={lead} />

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>What happened</h2>
              <span className="t-label subtle">absolute times — the gaps are the story</span>
            </div>
            <div className="card-body">
              <LeadTimeline events={data.timeline} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>Similar leads</h2>
              <span className="t-label subtle">
                {data.related.length > 0 ? 'strongest resemblance first' : 'none in this window'}
              </span>
            </div>
            <RelatedTable related={data.related} currency={lead.currency} onOpen={(id) => navigate(`/leads/${id}`)} />
          </div>
        </div>

        <div className="lead-detail-side">
          <IdentityCard lead={lead} />
          <AttributionCard lead={lead} />
          {lead.source === 'chat' && <ConversationCard lead={lead} />}
          <PeersCard peers={data.peers} cohortSize={data.cohort_size} from={data.cohort_from} to={data.cohort_to} />
        </div>
      </div>
    </>
  );
}

function IdentityCard({ lead }: { lead: LeadRow }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Who</h2>
      </div>
      <div className="card-body kv-list">
        <Kv label="Name" value={lead.name} />
        <Kv label="E-mail" value={lead.email} />
        <Kv label="Phone" value={lead.phone} />
        <Kv label="Country" value={lead.country} />
        <Kv label="City" value={lead.city} />
        <Kv label="Language" value={lead.language} />
        <Kv label="Owner" value={lead.owner} fallback="(unassigned)" />
        <Kv label="Deal value" value={lead.value_amount === null ? null : formatMoney(lead.value_amount, lead.currency)} />
        {lead.notes && <Kv label="Notes" value={lead.notes} />}
      </div>
    </div>
  );
}

function AttributionCard({ lead }: { lead: LeadRow }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>How they got here</h2>
      </div>
      <div className="card-body kv-list">
        <Kv label="Landing page" value={lead.landing_page} />
        <Kv label="Campaign" value={lead.utm_campaign} />
        <Kv label="Source" value={lead.utm_source} />
        <Kv label="Medium" value={lead.utm_medium} />
        <Kv label="Referrer" value={lead.referrer} />
        <Kv label="Form" value={lead.form_type} />
        <Kv label="Channel" value={lead.channel} />
      </div>
    </div>
  );
}

function ConversationCard({ lead }: { lead: LeadRow }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>The conversation</h2>
      </div>
      <div className="card-body kv-list">
        <Kv label="Started" value={lead.conversation_started_at ? formatDateTime(lead.conversation_started_at) : null} />
        <Kv label="Closed" value={lead.conversation_closed_at ? formatDateTime(lead.conversation_closed_at) : null} />
        <Kv label="Waited in queue" value={formatDuration(lead.queue_seconds)} />
        <Kv label="Answered in" value={lead.is_missed ? 'never' : formatDuration(lead.first_response_seconds)} />
        <Kv label="Ran for" value={formatDuration(lead.handling_seconds)} />
        <Kv label="Messages" value={
          lead.messages_in === null && lead.messages_out === null
            ? null
            : `${lead.messages_in ?? 0} in · ${lead.messages_out ?? 0} out`
        } />
        <Kv label="Rated" value={lead.csat_score === null ? null : `${lead.csat_score}/5`} />
        <Kv label="Chat status" value={lead.conversation_status} />
      </div>
    </div>
  );
}

/**
 * Where this lead sits among its peers.
 *
 * The cohort size is printed next to the heading rather than buried: a percentile computed against
 * eleven leads is arithmetic, not evidence, and the reader is the one who has to decide that.
 */
function PeersCard({
  peers, cohortSize, from, to,
}: { peers: LeadPeerStat[]; cohortSize: number; from: string; to: string }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Against its peers</h2>
        <span className="t-caption subtle">{cohortSize.toLocaleString('en-US')} leads · {from} → {to}</span>
      </div>
      <div className="card-body peer-list">
        {peers.map((peer) => (
          <div key={peer.key} className="peer-row">
            <div className="peer-head">
              <span className="t-label">{peer.label}</span>
              <span className="t-caption subtle tabular">
                {peer.percentile === null ? '—' : `top ${Math.max(1, Math.round(100 - peer.percentile))}%`}
              </span>
            </div>
            <div className="peer-bar">
              {peer.percentile !== null && (
                <div className="peer-bar-fill" style={{ width: `${Math.round(peer.percentile)}%` }} />
              )}
            </div>
            <div className="t-caption subtle">
              {peer.detail ?? '—'}
              {peer.cohort_median !== null && peer.value !== null && (
                <> · median {formatPeerValue(peer.key, peer.cohort_median)}</>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The peer bars carry mixed units, so each one is formatted by what it actually measures. */
function formatPeerValue(key: string, value: number): string {
  if (key === 'handling' || key === 'first_response') return formatDuration(value);
  if (key === 'csat') return `${value.toFixed(1)}/5`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function RelatedTable({
  related, currency, onOpen,
}: { related: RelatedLead[]; currency: string | null; onOpen: (id: string) => void }) {
  if (related.length === 0) {
    return (
      <div className="empty-state t-body">
        Nothing else in the last few months resembles this lead.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          That is itself a finding — this one did not arrive as part of a cohort.
        </div>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Lead</th>
            <th>Captured</th>
            <th>Stage</th>
            <th className="num">Value</th>
            <th>In common</th>
          </tr>
        </thead>
        <tbody>
          {related.map((row) => (
            <tr key={row.id} className="row-clickable" onClick={() => onOpen(row.id)}>
              <td>{row.label}</td>
              <td className="t-caption subtle">{formatDateTime(row.captured_at)}</td>
              <td><LeadStatusChip status={row.status} /></td>
              <td className="num">{row.value_amount === null ? <span className="subtle">—</span> : formatMoney(row.value_amount, currency)}</td>
              <td className="t-caption">{row.shared.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kv({ label, value, fallback = '—' }: { label: string; value: string | null | undefined; fallback?: string }) {
  return (
    <div className="kv-row">
      <span className="t-label muted">{label}</span>
      <span className={value ? 't-body' : 't-body subtle'}>{value || fallback}</span>
    </div>
  );
}
